import React from 'react';
import { createPortal } from 'react-dom';
import ModalDocumentActionButtons from '../common/ModalDocumentActionButtons';
import NeisLearningContentUploadTable from '../export/NeisLearningContentUploadTable';
import { saveNeisLearningContentUploadAsExcel } from '../../utils/neisLearningContentUploadExcelExport';
import {
  openDocumentExportPreviewFailureMessage,
  openDocumentExportPreviewInNewTab,
  openDocumentExportPreviewTabPlaceholder,
} from '../../utils/documentExportPreviewStorage';
import {
  buildNeisUploadLearningContentRows,
  buildSubscribedTrackFilterOptions,
  filterNeisLearningContentUploadRows,
  neisLearningContentUploadRowsHaveData,
  type NeisLearningContentUploadSourceRow,
  type NeisLearningContentUploadViewMode,
} from '../../utils/neisLearningContentUploadRows';
import { gradeHasSyncTimetableProgram } from '../../utils/trackProgramModeStorage';

export type NeisLearningContentUploadModalProps = {
  open: boolean;
  onClose: () => void;
  sourceRows: NeisLearningContentUploadSourceRow[];
  subscribedTrackNames: string[];
  termNumber: 1 | 2;
  loading?: boolean;
  workspaceId?: string;
  programGradeId?: string;
};

type NeisExcludeIncludeToggleProps = {
  label: string;
  included: boolean;
  onChange: (included: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
};

function sanitizeNeisExportFileToken(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48);
}

const NeisExcludeIncludeToggle: React.FC<NeisExcludeIncludeToggleProps> = ({
  label,
  included,
  onChange,
  ariaLabel,
  disabled = false,
}) => (
  <div className="cp-neis-learning-upload-filter-toggle" aria-label={ariaLabel}>
    <span className="cp-neis-learning-upload-filter-toggle-label">{label}</span>
    <button
      type="button"
      className={
        'team-auto-approve-toggle cp-neis-learning-upload-exclude-include-toggle' + (included ? ' on' : '')
      }
      onClick={() => onChange(!included)}
      aria-pressed={included}
      aria-label={`${label} ${included ? '포함' : '제외'}`}
      disabled={disabled}
    >
      <span className="team-auto-approve-toggle-slider" data-on={!included}>
        제외
      </span>
      <span className="team-auto-approve-toggle-slider" data-on={included}>
        포함
      </span>
    </button>
  </div>
);

const NeisLearningContentUploadModal: React.FC<NeisLearningContentUploadModalProps> = ({
  open,
  onClose,
  sourceRows,
  subscribedTrackNames,
  termNumber,
  loading = false,
  workspaceId = '',
  programGradeId = '',
}) => {
  const [selectedTrack, setSelectedTrack] = React.useState<string | null>(null);
  const [includeSyncTimetable, setIncludeSyncTimetable] = React.useState(true);
  const [includeActivity, setIncludeActivity] = React.useState(false);
  const [includeNonClass, setIncludeNonClass] = React.useState(false);
  const [includeDate, setIncludeDate] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<NeisLearningContentUploadViewMode>('annual-plan');
  const [exportBusy, setExportBusy] = React.useState(false);
  /** NEIS 업로드 모드 진입 전 동기화 시간표 포함 여부 — 해제 시 복원 */
  const syncTimetableBeforeNeisUploadRef = React.useRef(true);

  const showSyncTimetableToggle = React.useMemo(() => {
    if (gradeHasSyncTimetableProgram(workspaceId, programGradeId)) return true;
    return sourceRows.some((row) => row.isSyncTimetableMirror === true);
  }, [programGradeId, sourceRows, workspaceId]);
  const effectiveIncludeSyncTimetable = !showSyncTimetableToggle
    ? true
    : includeSyncTimetable;

  const toggleNeisUploadViewMode = React.useCallback(() => {
    if (viewMode === 'neis-upload') {
      setIncludeSyncTimetable(syncTimetableBeforeNeisUploadRef.current);
      setViewMode('annual-plan');
      return;
    }
    syncTimetableBeforeNeisUploadRef.current = includeSyncTimetable;
    setIncludeSyncTimetable(false);
    setViewMode('neis-upload');
  }, [includeSyncTimetable, viewMode]);

  const trackOptions = React.useMemo(
    () => buildSubscribedTrackFilterOptions(subscribedTrackNames),
    [subscribedTrackNames]
  );

  const subscribedTrackNameSet = React.useMemo(() => new Set(trackOptions), [trackOptions]);

  const filteredRows = React.useMemo(
    () =>
      filterNeisLearningContentUploadRows(sourceRows, {
        selectedTrack,
        includeActivity,
        includeNonClass,
        includeSyncTimetable: effectiveIncludeSyncTimetable,
        subscribedTrackNames: subscribedTrackNameSet,
      }),
    [
      effectiveIncludeSyncTimetable,
      includeActivity,
      includeNonClass,
      selectedTrack,
      sourceRows,
      subscribedTrackNameSet,
    ]
  );

  const neisUploadRows = React.useMemo(() => {
    const allTrackRows = filterNeisLearningContentUploadRows(sourceRows, {
      selectedTrack: null,
      includeActivity,
      includeNonClass,
      includeSyncTimetable: effectiveIncludeSyncTimetable,
      subscribedTrackNames: subscribedTrackNameSet,
    });
    return buildNeisUploadLearningContentRows(allTrackRows, trackOptions);
  }, [
    effectiveIncludeSyncTimetable,
    includeActivity,
    includeNonClass,
    sourceRows,
    subscribedTrackNameSet,
    trackOptions,
  ]);

  const displayRows = viewMode === 'neis-upload' ? neisUploadRows : filteredRows;
  const showDateInTable = viewMode === 'annual-plan' && includeDate;

  const headerTitle = React.useMemo(() => {
    if (viewMode === 'neis-upload') return 'NEIS 업로드 학습내용';
    return selectedTrack ? `${selectedTrack} 교육과정 연간지도계획` : '전과목 교육과정 연간지도계획';
  }, [selectedTrack, viewMode]);

  const canExport = !loading && displayRows.length > 0 && neisLearningContentUploadRowsHaveData(displayRows);

  const buildExportFileNameBase = React.useCallback(() => {
    const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
    const titlePart = sanitizeNeisExportFileToken(headerTitle) || '연간지도계획';
    return `연간지도_${termNumber}학기_${titlePart}_${dateLabel}`;
  }, [headerTitle, termNumber]);

  const printTitle = React.useMemo(() => `${termNumber}학기 ${headerTitle}`, [headerTitle, termNumber]);

  const openExportPreviewTab = React.useCallback(
    (action: 'pdf' | 'print') => {
      if (!canExport) return;
      const placeholder = openDocumentExportPreviewTabPlaceholder();
      const result = openDocumentExportPreviewInNewTab(
        {
          v: 1,
          kind: 'neis-learning-content-upload',
          fileNameBase: buildExportFileNameBase(),
          printTitle,
          data: {
            termNumber,
            headerTitle,
            showDateColumns: showDateInTable,
            viewMode,
            rows: displayRows,
          },
        },
        action,
        { preOpenedWindow: placeholder }
      );
      if (!result.ok) {
        alert(openDocumentExportPreviewFailureMessage(result.reason));
      }
    },
    [buildExportFileNameBase, canExport, displayRows, headerTitle, printTitle, showDateInTable, termNumber, viewMode]
  );

  const handleSavePdf = React.useCallback(() => {
    openExportPreviewTab('pdf');
  }, [openExportPreviewTab]);

  const handleSaveExcel = React.useCallback(async () => {
    if (!canExport) return;
    setExportBusy(true);
    try {
      await saveNeisLearningContentUploadAsExcel({
        rows: displayRows,
        headerTitle,
        termNumber,
        showDateColumns: showDateInTable,
        viewMode,
        fileName: `${buildExportFileNameBase()}.xlsx`,
      });
    } catch (error) {
      console.error('엑셀 저장 실패:', error);
      alert('엑셀 저장에 실패했습니다.');
    } finally {
      setExportBusy(false);
    }
  }, [buildExportFileNameBase, canExport, displayRows, headerTitle, showDateInTable, termNumber, viewMode]);

  const handlePrint = React.useCallback(() => {
    openExportPreviewTab('print');
  }, [openExportPreviewTab]);

  React.useEffect(() => {
    if (!open) return;
    setSelectedTrack(null);
    setIncludeSyncTimetable(true);
    syncTimetableBeforeNeisUploadRef.current = true;
    setIncludeActivity(false);
    setIncludeNonClass(false);
    setIncludeDate(false);
    setViewMode('annual-plan');
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="cp-modal-backdrop cp-neis-learning-upload-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        className="cp-modal cp-neis-learning-upload-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-neis-learning-upload-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="cp-modal-document-header cp-neis-learning-upload-header">
          <h3 id="cp-neis-learning-upload-title" className="cp-neis-learning-upload-header-title">
            <span
              className={`cp-annual-cross-topic-detail-scope cp-annual-cross-topic-detail-scope--term${termNumber}`}
            >
              {termNumber}학기
            </span>
            <span className="cp-neis-learning-upload-header-title-text">{headerTitle}</span>
          </h3>
          <div className="cp-neis-learning-upload-header-actions">
            <button
              type="button"
              className={
                'cp-neis-learning-upload-mode-btn' +
                (viewMode === 'neis-upload' ? ' cp-neis-learning-upload-mode-btn--active' : '')
              }
              onClick={toggleNeisUploadViewMode}
              aria-pressed={viewMode === 'neis-upload'}
            >
              NEIS 업로드 학습내용
            </button>
            <ModalDocumentActionButtons
              onSavePdf={() => void handleSavePdf()}
              onSaveExcel={() => void handleSaveExcel()}
              onPrint={() => void handlePrint()}
              onClose={onClose}
              saveDisabled={!canExport}
              saveExcelDisabled={!canExport}
              printDisabled={!canExport}
              busy={exportBusy}
            />
          </div>
        </div>

        <div className="cp-neis-learning-upload-filters-bar" role="toolbar" aria-label="편제·동기화·활동·비수업·날짜 필터">
          <div className="cp-neis-learning-upload-filter-group">
            <span className="cp-neis-learning-upload-filter-label">편제</span>
            <div className="cp-neis-learning-upload-filter-chips">
              <button
                type="button"
                className={`cp-neis-learning-upload-filter-chip${selectedTrack === null ? ' cp-neis-learning-upload-filter-chip--active' : ''}`}
                onClick={() => setSelectedTrack(null)}
                disabled={viewMode === 'neis-upload'}
              >
                전체
              </button>
              {trackOptions.map((trackName) => (
                <button
                  key={trackName}
                  type="button"
                  className={`cp-neis-learning-upload-filter-chip${selectedTrack === trackName ? ' cp-neis-learning-upload-filter-chip--active' : ''}`}
                  onClick={() => setSelectedTrack(trackName)}
                  disabled={viewMode === 'neis-upload'}
                >
                  {trackName}
                </button>
              ))}
            </div>
          </div>
          <div className="cp-neis-learning-upload-filter-row-secondary">
            {showSyncTimetableToggle ? (
              <NeisExcludeIncludeToggle
                label="동기화 시간표"
                included={includeSyncTimetable}
                onChange={setIncludeSyncTimetable}
                ariaLabel="동기화 시간표 포함 여부"
              />
            ) : null}
            <NeisExcludeIncludeToggle
              label="활동"
              included={includeActivity}
              onChange={setIncludeActivity}
              ariaLabel="활동 포함 여부"
            />
            <NeisExcludeIncludeToggle
              label="비수업"
              included={includeNonClass}
              onChange={setIncludeNonClass}
              ariaLabel="비수업 포함 여부"
            />
            <NeisExcludeIncludeToggle
              label="날짜"
              included={includeDate}
              onChange={setIncludeDate}
              ariaLabel="날짜 열 표시"
              disabled={viewMode === 'neis-upload'}
            />
          </div>
        </div>

        <div className="cp-neis-learning-upload-scroll">
          {loading ? (
            <p className="page-desc cp-neis-learning-upload-scroll-message" role="status" aria-busy="true">
              데이터 불러오는 중…
            </p>
          ) : trackOptions.length === 0 ? (
            <p className="page-desc cp-neis-learning-upload-scroll-message">이 클래스가 구독 중인 편제가 없습니다.</p>
          ) : displayRows.length === 0 ? (
            <p className="page-desc cp-neis-learning-upload-scroll-message">선택한 조건에 맞는 학습내용이 없습니다.</p>
          ) : (
            <div className="cp-neis-learning-upload-table-wrap">
              <NeisLearningContentUploadTable
                rows={displayRows}
                showDateColumns={showDateInTable}
                variant={viewMode}
              />
            </div>
          )}
        </div>

        <div className="cp-modal-footer cp-neis-learning-upload-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NeisLearningContentUploadModal;
